import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { TournamentShell } from './layout/AppShell';
import { TournamentList } from './pages/TournamentList';
import { NewTournament } from './pages/NewTournament';
import { TeamsPage } from './pages/Teams';
import { CourtsPage } from './pages/Courts';
import { SettingsPage } from './pages/Settings';
const ExportPage = lazy(() => import('./pages/Export').then((m) => ({ default: m.ExportPage })));
import { VorrundePage } from './pages/Vorrunde';
import { SpielePage } from './pages/Spiele';
import { TabellePage } from './pages/Tabelle';
const KOPage = lazy(() => import('./pages/KO').then((m) => ({ default: m.KOPage })));
const KoRundenPage = lazy(() => import('./pages/KoRunden').then((m) => ({ default: m.KoRundenPage })));
const BeamerPage = lazy(() => import('./pages/Beamer').then((m) => ({ default: m.BeamerPage })));
const SeasonList = lazy(() => import('./pages/Saison').then((m) => ({ default: m.SeasonList })));
const SeasonPage = lazy(() => import('./pages/Saison').then((m) => ({ default: m.SeasonPage })));
const JoinPage = lazy(() => import('./pages/Join').then((m) => ({ default: m.JoinPage })));
const AnmeldenPage = lazy(() => import('./pages/Anmelden').then((m) => ({ default: m.AnmeldenPage })));
const LoginPage = lazy(() => import('./pages/Login').then((m) => ({ default: m.LoginPage })));
const KontoPage = lazy(() => import('./pages/Konto').then((m) => ({ default: m.KontoPage })));
const AdminPage = lazy(() => import('./pages/Admin').then((m) => ({ default: m.AdminPage })));
const PlakatPage = lazy(() => import('./pages/Plakat').then((m) => ({ default: m.PlakatPage })));
const DruckPage = lazy(() => import('./pages/Druck').then((m) => ({ default: m.DruckPage })));
const LivePage = lazy(() => import('./pages/Live').then((m) => ({ default: m.LivePage })));
const StartPage = lazy(() => import('./pages/Start').then((m) => ({ default: m.StartPage })));
const RechtlichesPage = lazy(() => import('./pages/Rechtliches').then((m) => ({ default: m.RechtlichesPage })));
const WillkommenPage = lazy(() => import('./pages/Willkommen').then((m) => ({ default: m.WillkommenPage })));
import { UpdatePrompt } from './pwa/UpdatePrompt';
import { Tour } from './tour/Tour';
import { DialogHost } from './ui/dialogs';
import { useT } from './i18n';
import { useAuth } from './store/AuthProvider';
import { db } from './store/db';
import { welcomeSeen } from './lib/willkommen';

export function App() {
  const t = useT();
  return (
    <Suspense fallback={<div className="p-8 text-dim">{t('Lade …')}</div>}>
      <Routes>
        <Route path="/" element={<Einstieg />} />
        <Route path="/willkommen" element={<WillkommenPage />} />
        <Route path="/neu" element={<NewTournament />} />
        <Route path="/saison" element={<SeasonList />} />
        <Route path="/saison/:id" element={<SeasonPage />} />
        <Route path="/join" element={<JoinPage />} />
        <Route path="/anmelden" element={<AnmeldenPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/konto" element={<KontoPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/plakat/:id" element={<PlakatPage />} />
        <Route path="/druck/:id" element={<DruckPage />} />
        <Route path="/live" element={<LivePage />} />
        <Route path="/rechtliches" element={<RechtlichesPage />} />
        <Route path="/t/:id" element={<TournamentShell />}>
          <Route index element={<IndexRedirect />} />
          <Route path="start" element={<StartPage />} />
          <Route path="teams" element={<TeamsPage />} />
          <Route path="vorrunde" element={<VorrundePage />} />
          <Route path="ko" element={<KOPage />} />
          <Route path="ko-runden" element={<KoRundenPage />} />
          <Route path="spiele" element={<SpielePage />} />
          <Route path="tabelle" element={<TabellePage />} />
          <Route path="felder" element={<CourtsPage />} />
          <Route path="beamer" element={<BeamerPage />} />
          <Route path="export" element={<ExportPage />} />
          <Route path="einstellungen" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <UpdatePrompt />
      <Tour />
      <DialogHost />
    </Suspense>
  );
}

/**
 * `#/`: Wer neu ist, sieht die Willkommensseite — wer schon Turniere auf dem
 * Gerät hat, angemeldet ist oder einmal „Zur App" gedrückt hat
 * (`airmail.welcome`, lib/willkommen.ts), bekommt wie bisher die Turnierliste.
 * Helfer mit Link, Zuschauer und Anmeldeseiten kommen hier gar nicht vorbei.
 *
 * Die Adresse bleibt `#/`: Die Willkommensseite ist hier eingebettet, „Zur
 * App" schaltet nur den Zustand um. So bleiben Links auf `#/` stabil, und die
 * Tour, die in der Liste beginnt, findet ihr Ziel gleich nach dem Umschalten.
 */
function Einstieg() {
  const t = useT();
  const auth = useAuth();
  const [zeige, setZeige] = useState<'lade' | 'willkommen' | 'liste'>(() => (welcomeSeen() ? 'liste' : 'lade'));
  useEffect(() => {
    if (zeige !== 'lade') return;
    if (auth.user) return setZeige('liste');
    let alive = true;
    db.tournaments.count().then(
      (n) => alive && setZeige(n > 0 ? 'liste' : 'willkommen'),
      // Speicher nicht lesbar — dann lieber die App als eine Vorstellung, die niemand weiterklicken kann
      () => alive && setZeige('liste'),
    );
    return () => {
      alive = false;
    };
  }, [zeige, auth.user]);
  if (zeige === 'lade') return <div className="p-8 text-dim">{t('Lade …')}</div>;
  if (zeige === 'willkommen') return <WillkommenPage onWeiter={() => setZeige('liste')} />;
  return <TournamentList />;
}

/**
 * Turnier geöffnet: Auf dem Handy zuerst die Einstiegsseite (große Knöpfe für
 * Helfer, docs/notes/start.md), auf breiten Bildschirmen wie bisher „Teams“ —
 * dort liegt die Seitenleiste ohnehin sichtbar daneben.
 */
function IndexRedirect() {
  const narrow = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  return <Navigate to={narrow ? 'start' : 'teams'} replace />;
}
